(() => {
  const normalize = (value) =>
    (value || '')
      .replace(/\s+/g, '')
      .trim();

  const getLocale = () => {
    const documentLocale = document.documentElement.lang || '';
    const shopifyLocale =
      window.Shopify && window.Shopify.locale
        ? window.Shopify.locale
        : '';

    const locale = String(
      documentLocale || shopifyLocale || 'ja'
    ).toLowerCase();

    if (locale.startsWith('en')) return 'en';
    if (locale.startsWith('ko')) return 'ko';

    return 'ja';
  };

  const locale = getLocale();
  const isJapan = locale === 'ja';

  const translations = {
    ja: {
      sizeOptionName: 'サイズ',
      sizeGuide: 'サイズガイド',
      sizeGuideAria: 'サイズガイドを開く',
      addToCart: 'カートに入れる',
      productDetails: '商品について',
      sizeFit: 'サイズ・フィット',
      materialWear: '素材・着用目安',
      shipping: '配送について',
      purchaseReturnsAria: '購入・返品情報',
      lowStock: '残りわずか',
      inStock: '在庫あり',
      soldOut: 'SOLD OUT',
      continueShopping: 'COLLECTIONへ戻る',
      emptyCart: 'CART IS EMPTY',
      total: '合計',
      checkout: '購入手続きに進む',
    },
    en: {
      sizeOptionName: 'Size',
      sizeGuide: 'Size Guide',
      sizeGuideAria: 'Open size guide',
      addToCart: 'Add to cart',
      productDetails: 'About the Product',
      sizeFit: 'Size & Fit',
      materialWear: 'Material & Wear Guide',
      shipping: 'Shipping',
      purchaseReturnsAria: 'Purchase and returns information',
      lowStock: 'Low stock',
      inStock: 'In stock',
      soldOut: 'SOLD OUT',
      continueShopping: 'Back to Collection',
      emptyCart: 'CART IS EMPTY',
      total: 'Total',
      checkout: 'Proceed to Checkout',
    },
    ko: {
      sizeOptionName: '사이즈',
      sizeGuide: '사이즈 가이드',
      sizeGuideAria: '사이즈 가이드 열기',
      addToCart: '장바구니에 담기',
      productDetails: '상품 정보',
      sizeFit: '사이즈 · 핏',
      materialWear: '소재 · 착용 가이드',
      shipping: '배송 안내',
      purchaseReturnsAria: '구매 및 반품 정보',
      lowStock: '재고 소량',
      inStock: '재고 있음',
      soldOut: '품절',
      continueShopping: '컬렉션으로 돌아가기',
      emptyCart: '장바구니가 비어 있습니다',
      total: '합계',
      checkout: '결제하기',
    },
  };

  const translation =
    translations[locale] || translations.ja;

  const getRouteRoot = () => {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return root.endsWith('/') ? root : `${root}/`;
  };

  const routeRoot = getRouteRoot();
  const cartUrl = `${routeRoot}cart.js`;
  const cartPageUrl = `${routeRoot}cart`;
  const cartUpdateUrl = `${routeRoot}cart/update.js`;

  const deliveryFeeHandle = '宅急便料金-100円';

  const fetchCart = async () => {
    const response = await fetch(cartUrl, {
      headers: {
        Accept: 'application/json',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(
        `Cart fetch failed: ${response.status}`
      );
    }

    return response.json();
  };

  const updateCart = async (payload) => {
    const response = await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `Cart update failed: ${response.status}`
      );
    }

    return response.json();
  };

  const isDeliveryFeeItem = (item) => {
    if (!item) return false;

    const properties = item.properties || {};
    const hiddenProperty = String(
      properties._hal_delivery_fee || ''
    ).toLowerCase();

    return (
      item.handle === deliveryFeeHandle ||
      item.product_handle === deliveryFeeHandle ||
      hiddenProperty === 'true'
    );
  };

  const getMerchandiseCount = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      if (isDeliveryFeeItem(item)) return total;

      return total + Number(item.quantity || 0);
    }, 0);
  };

  const setText = (element, value) => {
    if (!element || typeof value !== 'string') return;

    if (element.textContent !== value) {
      element.textContent = value;
    }
  };

  const findAccordion = (root, name) =>
    root.querySelector(
      `.hal-product-accordion[data-hal-accordion="${name}"]`
    );

  const openAccordion = (root, name) => {
    const accordion = findAccordion(root, name);

    if (!accordion) return;

    const details = accordion.querySelector('details');
    const summary = accordion.querySelector('summary');

    if (details && !details.open) {
      details.open = true;
    }

    window.requestAnimationFrame(() => {
      accordion.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      window.setTimeout(() => {
        summary?.focus({
          preventScroll: true,
        });
      }, 450);
    });
  };

  const knownSizeOptionNames = Object.values(
    translations
  ).map((item) => normalize(item.sizeOptionName));

  const knownAddToCartLabels = [
    'カートに追加する',
    'カートに追加',
    'カートに入れる',
    'Add to cart',
    'Addtocart',
    '장바구니에 담기',
    '장바구니 담기',
    '장바구니에 추가',
  ].map(normalize);

  const findSizeOption = (root) => {
    const groups = root.querySelectorAll(
      '.product-form__input'
    );

    return Array.from(groups).find((group) => {
      const namedControls = group.querySelectorAll(
        'input[data-option-name], select[data-option-name]'
      );

      const hasKnownOptionName = Array.from(
        namedControls
      ).some((control) =>
        knownSizeOptionNames.includes(
          normalize(
            control.getAttribute('data-option-name')
          )
        )
      );

      if (hasKnownOptionName) return true;

      const label = group.querySelector(
        ':scope > .form__label'
      );

      const normalizedLabel = normalize(
        label?.textContent
      );

      return knownSizeOptionNames.some((optionName) =>
        normalizedLabel.startsWith(optionName)
      );
    });
  };

  const addSizeGuideLink = (root) => {
    const sizeOption = findSizeOption(root);
    const sizeAccordion = findAccordion(root, 'size');

    if (!sizeOption || !sizeAccordion) return;

    sizeOption.setAttribute(
      'data-hal-size-option',
      ''
    );

    let button = sizeOption.querySelector(
      ':scope > .hal-size-guide-link'
    );

    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'hal-size-guide-link';

      button.addEventListener('click', () => {
        openAccordion(root, 'size');
      });

      sizeOption.appendChild(button);
    }

    const buttonHtml =
      `<span>${translation.sizeGuide}</span>` +
      '<span class="hal-size-guide-link__arrow" aria-hidden="true">→</span>';

    button.setAttribute(
      'aria-label',
      translation.sizeGuideAria
    );

    if (button.innerHTML !== buttonHtml) {
      button.innerHTML = buttonHtml;
    }
  };

  const groupQuantityAndInventory = (root) => {
    const quantity = root.querySelector(
      '.product-form__quantity'
    );

    const inventory = root.querySelector(
      '.product__inventory'
    );

    if (!quantity || !inventory) return;
    if (quantity.closest('.hal-quantity-stock-row')) {
      return;
    }

    const parent = quantity.parentElement;

    if (
      !parent ||
      inventory.parentElement !== parent
    ) {
      return;
    }

    const row = document.createElement('div');
    row.className = 'hal-quantity-stock-row';

    parent.insertBefore(row, quantity);
    row.appendChild(quantity);
    row.appendChild(inventory);
  };

  const updateAddToCartLabel = (root) => {
    const button = root.querySelector(
      '.product-form__submit'
    );

    if (!button || button.disabled) return;

    const label = button.querySelector(
      'span:not(.loading__spinner)'
    );

    if (!label) return;

    const currentLabel = normalize(
      label.textContent
    );

    if (
      !knownAddToCartLabels.includes(currentLabel)
    ) {
      return;
    }

    const nextLabel = translation.addToCart;

    if (
      currentLabel !== normalize(nextLabel)
    ) {
      label.textContent = nextLabel;
    }
  };

  const translateAccordionLabels = (root) => {
    const accordionLabels = {
      details: translation.productDetails,
      size: translation.sizeFit,
      material: translation.materialWear,
      shipping: translation.shipping,
    };

    Object.entries(accordionLabels).forEach(
      ([name, label]) => {
        const accordion = findAccordion(root, name);

        const title = accordion?.querySelector(
          'summary > span:first-child'
        );

        setText(title, label);
      }
    );
  };

  const translatePurchaseNotes = (root) => {
    const purchaseNotes = root.querySelector(
      '.hal-purchase-notes'
    );

    if (!purchaseNotes) return;

    if (
      purchaseNotes.getAttribute('aria-label') !==
      translation.purchaseReturnsAria
    ) {
      purchaseNotes.setAttribute(
        'aria-label',
        translation.purchaseReturnsAria
      );
    }
  };

  const translateInventoryLabel = (root) => {
    const inventory = root.querySelector(
      '.hal-inventory-status'
    );

    const label = inventory?.querySelector(
      '.hal-inventory-label'
    );

    if (!inventory || !label) return;

    let nextLabel = '';

    if (
      inventory.classList.contains(
        'hal-inventory-status--low'
      )
    ) {
      nextLabel = translation.lowStock;
    } else if (
      inventory.classList.contains(
        'hal-inventory-status--in-stock'
      ) ||
      inventory.classList.contains(
        'hal-inventory-status--continue'
      )
    ) {
      nextLabel = translation.inStock;
    } else if (
      inventory.classList.contains(
        'hal-inventory-status--sold-out'
      )
    ) {
      nextLabel = translation.soldOut;
    }

    if (nextLabel) {
      setText(label, nextLabel);
    }
  };

  const bindAccordionTriggers = (root) => {
    root
      .querySelectorAll(
        '[data-hal-open-accordion]'
      )
      .forEach((trigger) => {
        if (
          trigger.dataset.halBound === 'true'
        ) {
          return;
        }

        trigger.dataset.halBound = 'true';

        trigger.addEventListener('click', () => {
          openAccordion(
            root,
            trigger.dataset.halOpenAccordion
          );
        });
      });
  };

  const disableProductMediaZoom = (root) => {
    if (
      root.dataset.halMediaZoomDisabled !==
      'true'
    ) {
      root.dataset.halMediaZoomDisabled =
        'true';

      root.addEventListener(
        'click',
        (event) => {
          const target = event.target;

          if (!(target instanceof Element)) {
            return;
          }

          const opener = target.closest(
            'modal-opener.product__modal-opener--image, .product__media-toggle'
          );

          if (
            !opener ||
            !root.contains(opener)
          ) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
        },
        true
      );

      root.addEventListener(
        'keydown',
        (event) => {
          if (
            event.key !== 'Enter' &&
            event.key !== ' '
          ) {
            return;
          }

          const target = event.target;

          if (!(target instanceof Element)) {
            return;
          }

          const opener = target.closest(
            'modal-opener.product__modal-opener--image, .product__media-toggle'
          );

          if (
            !opener ||
            !root.contains(opener)
          ) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
        },
        true
      );
    }

    root
      .querySelectorAll(
        'modal-opener.product__modal-opener--image'
      )
      .forEach((opener) => {
        opener.removeAttribute('data-modal');

        opener
          .querySelectorAll(
            '.product__media-icon, .product__media-toggle'
          )
          .forEach((control) => {
            control.remove();
          });
      });

    const sectionId = root.dataset.section;

    if (!sectionId) return;

    document
      .getElementById(
        `ProductModal-${sectionId}`
      )
      ?.remove();
  };

  const findProductForm = (root) => {
    const addButtons = Array.from(
      root.querySelectorAll(
        'button[name="add"], input[name="add"], .product-form__submit'
      )
    );

    const visibleButton = addButtons.find(
      (button) => {
        const rect =
          button.getBoundingClientRect();

        return (
          rect.width > 0 &&
          rect.height > 0
        );
      }
    );

    const addButton =
      visibleButton || addButtons[0];

    if (addButton?.form) {
      return addButton.form;
    }

    return root.querySelector(
      'form[action*="/cart/add"]'
    );
  };

  const bindInternationalCartRedirect = async (
    root
  ) => {
    if (isJapan) return;

    if (
      root.dataset
        .halCartRedirectInitializing === 'true'
    ) {
      return;
    }

    root.dataset.halCartRedirectInitializing =
      'true';

    try {
      const form = findProductForm(root);

      if (!form) {
        window.setTimeout(() => {
          delete root.dataset
            .halCartRedirectInitializing;

          bindInternationalCartRedirect(root);
        }, 250);

        return;
      }

      if (
        form.dataset
          .halCartRedirectBound === 'true'
      ) {
        return;
      }

      let currentMerchandiseCount = 0;

      try {
        const cart = await fetchCart();

        currentMerchandiseCount =
          getMerchandiseCount(cart);
      } catch (error) {
        console.error(
          '[HAL cart redirect init]',
          error
        );
      }

      form.dataset.halCartRedirectBound = 'true';

      let watching = false;

      form.addEventListener(
        'submit',
        async () => {
          if (watching) return;

          watching = true;

          const baseline =
            currentMerchandiseCount;

          const startedAt = Date.now();

          try {
            while (
              Date.now() - startedAt < 10000
            ) {
              await new Promise((resolve) => {
                window.setTimeout(
                  resolve,
                  300
                );
              });

              const cart = await fetchCart();

              const nextCount =
                getMerchandiseCount(cart);

              currentMerchandiseCount =
                nextCount;

              if (nextCount > baseline) {
                window.location.assign(
                  cartPageUrl
                );

                return;
              }
            }
          } catch (error) {
            console.error(
              '[HAL cart redirect]',
              error
            );
          } finally {
            watching = false;
          }
        },
        true
      );
    } finally {
      delete root.dataset
        .halCartRedirectInitializing;
    }
  };

  const initializeProductRoot = (root) => {
    disableProductMediaZoom(root);
    translateAccordionLabels(root);
    translatePurchaseNotes(root);
    translateInventoryLabel(root);
    addSizeGuideLink(root);
    groupQuantityAndInventory(root);
    updateAddToCartLabel(root);
    bindAccordionTriggers(root);
    bindInternationalCartRedirect(root);
  };

  const formatCartMoney = (cart) => {
    const activeCurrency =
      cart.currency ||
      window.Shopify?.currency?.active ||
      (locale === 'ko'
        ? 'KRW'
        : locale === 'en'
          ? 'USD'
          : 'JPY');

    const formatterLocale =
      locale === 'ko'
        ? 'ko-KR'
        : locale === 'en'
          ? 'en-US'
          : 'ja-JP';

    const amount =
      Number(cart.total_price || 0) / 100;

    return new Intl.NumberFormat(
      formatterLocale,
      {
        style: 'currency',
        currency: activeCurrency,
      }
    ).format(amount);
  };

  const translateCartLabels = () => {
    document
      .querySelectorAll('.hal-cart-continue')
      .forEach((element) => {
        setText(
          element,
          `${translation.continueShopping}\u00a0→`
        );
      });

    const emptyText = document.querySelector(
      '.hal-cart-empty .cart__empty-text'
    );

    setText(
      emptyText,
      translation.emptyCart
    );

    document
      .querySelectorAll(
        '.hal-cart-empty-button span:first-child'
      )
      .forEach((element) => {
        setText(
          element,
          translation.continueShopping
        );
      });

    document
      .querySelectorAll('.totals__total')
      .forEach((element) => {
        setText(element, translation.total);
      });

    document
      .querySelectorAll(
        '.hal-cart-checkout'
      )
      .forEach((element) => {
        setText(element, translation.checkout);
      });

    if (!isJapan) {
      document
        .querySelectorAll(
          '.hal-cart-packaging-row'
        )
        .forEach((element) => {
          element.style.display = 'none';
          element.setAttribute(
            'aria-hidden',
            'true'
          );
        });

      const errorBox =
        document.querySelector('#cart-errors');

      if (errorBox) {
        errorBox.textContent = '';
        errorBox.style.display = 'none';
      }
    }
  };

  let cartCleanupRunning = false;

  const cleanupInternationalCart =
    async () => {
      if (isJapan || cartCleanupRunning) {
        return;
      }

      cartCleanupRunning = true;

      try {
        let cart = await fetchCart();

        const feeItems = Array.isArray(
          cart.items
        )
          ? cart.items.filter(
              isDeliveryFeeItem
            )
          : [];

        const currentAttributes =
          cart.attributes || {};

        const packagingMethod =
          currentAttributes['梱包方法'];

        const payload = {};
        let shouldUpdate = false;

        if (feeItems.length > 0) {
          const updates = {};

          feeItems.forEach((item) => {
            updates[item.key] = 0;
          });

          payload.updates = updates;
          shouldUpdate = true;
        }

        if (packagingMethod) {
          payload.attributes = {
            ...currentAttributes,
            '梱包方法': '',
          };

          shouldUpdate = true;
        }

        if (shouldUpdate) {
          cart = await updateCart(payload);
        }

        translateCartLabels();

        const totalElement =
          document.querySelector(
            '[data-hal-cart-total]'
          );

        if (totalElement) {
          setText(
            totalElement,
            formatCartMoney(cart)
          );
        }

        const checkoutButton =
          document.querySelector('#checkout');

        if (checkoutButton) {
          checkoutButton.disabled =
            getMerchandiseCount(cart) === 0;
        }

        if (feeItems.length > 0) {
          window.location.reload();
        }
      } catch (error) {
        console.error(
          '[HAL international cart cleanup]',
          error
        );
      } finally {
        cartCleanupRunning = false;
      }
    };

  let cartRefreshTimer;

  const scheduleCartRefresh = () => {
    window.clearTimeout(cartRefreshTimer);

    cartRefreshTimer =
      window.setTimeout(async () => {
        translateCartLabels();

        if (!isJapan) {
          await cleanupInternationalCart();

          try {
            const cart = await fetchCart();

            const totalElement =
              document.querySelector(
                '[data-hal-cart-total]'
              );

            if (totalElement) {
              setText(
                totalElement,
                formatCartMoney(cart)
              );
            }

            const checkoutButton =
              document.querySelector(
                '#checkout'
              );

            if (checkoutButton) {
              checkoutButton.disabled =
                getMerchandiseCount(cart) ===
                0;
            }
          } catch (error) {
            console.error(
              '[HAL international cart refresh]',
              error
            );
          }
        }
      }, 250);
  };

  const initializeCartPage = () => {
    const cartItems =
      document.querySelector('cart-items');

    const cartFooter =
      document.querySelector(
        '#main-cart-footer'
      );

    if (!cartItems && !cartFooter) return;

    translateCartLabels();

    if (!isJapan) {
      cleanupInternationalCart();

      window.setTimeout(
        cleanupInternationalCart,
        1200
      );
    }

    [cartItems, cartFooter]
      .filter(Boolean)
      .forEach((element) => {
        const observer =
          new MutationObserver(
            scheduleCartRefresh
          );

        observer.observe(element, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: [
            'disabled',
            'class',
          ],
        });
      });

    document.addEventListener(
      'change',
      (event) => {
        if (
          event.target?.matches(
            '.quantity__input'
          )
        ) {
          scheduleCartRefresh();
        }
      }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (
          event.target?.closest(
            'cart-remove-button, [data-cart-remove]'
          )
        ) {
          scheduleCartRefresh();
        }
      }
    );
  };

  const boot = () => {
    document.body.classList.remove(
      'hal-has-sticky-cart'
    );

    document
      .querySelectorAll(
        'product-info[id^="MainProduct-"]'
      )
      .forEach((root) => {
        initializeProductRoot(root);

        const observer =
          new MutationObserver(() => {
            initializeProductRoot(root);
          });

        observer.observe(root, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: [
            'disabled',
            'class',
            'aria-disabled',
          ],
        });
      });

    initializeCartPage();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true,
      }
    );
  } else {
    boot();
  }
})();